import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLORS } from '../../../../../../constants/styles';
import { METRIC_HIGHER_IS_BETTER, STATUS_COLORS } from '../../../../constants';
import { churnHistoryConfig } from '../../../config/metricHistory';
import { IChurnDetailsData, IModalFilterContext } from '../../../types';
import { formatActiveStatuses } from '../../../utils/formatActiveStatuses';
import { ExplainerBox } from '../../shared/ExplainerBox';
import { FormulaBox } from '../../shared/FormulaBox';
import { FormulaLine } from '../../shared/FormulaLine';
import { MetricDetailsModal } from '../../shared/MetricDetailsModal';
import { MetricHistoryChart } from '../../shared/MetricHistoryChart';
import { MetricsDivider } from '../../shared/MetricsDivider';
import { StatRow } from '../../shared/StatRow';
import { ThresholdLegend } from '../../shared/ThresholdLegend';

interface IChurnDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  variant: 'period' | 'quarterly';
  data: IChurnDetailsData | null;
  filter: IModalFilterContext;
}

export const ChurnDetailsModal: FC<IChurnDetailsModalProps> = ({
  visible,
  onClose,
  variant,
  data,
  filter,
}) => {
  const { t } = useTranslation('dashboard.RecruiterDashboard.churnModal');
  if (!data) return null;

  const netChange = data.enrollmentCount - data.dropoutCount;
  const netColor =
    netChange > 0
      ? STATUS_COLORS.good.fill
      : netChange < 0
      ? STATUS_COLORS.delta.negative
      : BRAND_COLORS.secondary.lightSage;

  return (
    <MetricDetailsModal
      visible={visible}
      onClose={onClose}
      data={data}
      title={t('title')}
      tooltip={t('tooltip')}
      sectionLabel={
        variant === 'quarterly'
          ? t('sectionThisQuarter')
          : t('sectionCurrentPeriod')
      }
      headerValue={`${data.churnRate}%`}
      explainer={
        <ExplainerBox>
          <strong style={{ color: '#fff' }}>{t('explainerDropoutTerm')}</strong>{' '}
          {t('explainerDropoutDef', {
            statuses: formatActiveStatuses(filter.activeDonorStatuses),
          })}{' '}
          <strong style={{ color: '#fff' }}>
            {t('explainerTransientTerm')}
          </strong>{' '}
          {t('explainerTransientDesc')}{' '}
          <strong style={{ color: STATUS_COLORS.good.fill }}>
            {t('explainerNegativeTerm')}
          </strong>{' '}
          {t('explainerNegativeDesc')}
        </ExplainerBox>
      }
      historySection={
        <MetricHistoryChart
          config={churnHistoryConfig}
          live={data}
          filter={filter}
          extra={{}}
          translationNs="dashboard.RecruiterDashboard.churnModal"
          isQuarterly={variant === 'quarterly'}
        />
      }
    >
      <StatRow label={t('statActiveAtStart')} value={data.activeAtStart} />
      <StatRow
        label={t('statDropouts')}
        value={`−${data.dropoutCount}`}
        color={STATUS_COLORS.delta.negative}
        indent
      />
      <StatRow
        label={t('statEnrollments')}
        value={`+${data.enrollmentCount}`}
        color={STATUS_COLORS.delta.positive}
        indent
      />
      <MetricsDivider />
      <StatRow
        label={t('statNetChange')}
        value={netChange >= 0 ? `+${netChange}` : `${netChange}`}
        color={netColor}
        bold
      />
      <FormulaBox>
        <FormulaLine>{t('formula')}</FormulaLine>
        <FormulaLine marginTop={4}>
          = ({data.dropoutCount} − {data.enrollmentCount}) ÷{' '}
          {data.activeAtStart} × 100{' = '}
          <span style={{ color: data.statusColor, fontWeight: 700 }}>
            {data.churnRate}%
          </span>
        </FormulaLine>
      </FormulaBox>
      <ThresholdLegend
        breakpoints={data.breakpoints}
        higherIsBetter={METRIC_HIGHER_IS_BETTER.churn}
        labels={data.breakpointLabels}
      />
    </MetricDetailsModal>
  );
};

export default ChurnDetailsModal;
